/**
 * Son tıklanan öğe ve "sinirli tıklama" tespiti.
 *
 * Her tıklamayı capture fazında dinler ve tıklanan eylemin görünen adını
 * tutar (`button "Filtreyi uygula"` gibi); hata raporu "ne tıklanmışken"
 * sorusunu buradan cevaplar. Aynı öğeye 1 saniyede 3 tık "sinirli tıklama"
 * sayılır: kullanıcı bastığı şeyin çalışmadığını düşünüyor, arkada hata
 * olmasa bile. Art arda basılması normal olan bir kontrol (sayaç, sayfalama)
 * `data-no-rage` ile muaf tutulur. Input değerleri asla okunmaz.
 */

const ACTIONABLE =
  'button, a, [role="button"], [role="menuitem"], [role="tab"], [role="option"], summary, ' +
  'input[type="submit"], input[type="button"], input[type="checkbox"], select, [data-testid]';
const LABEL_MAX = 80;
const RAGE_CLICKS = 3;
const RAGE_WINDOW_MS = 1000;
const RAGE_COOLDOWN_MS = 10_000;
const ACTION_FRESH_MS = 15_000;

let last: { label: string; at: number } | null = null;
let burst: { el: Element | null; times: number[] } = { el: null, times: [] };
let rageReported = new WeakMap<Element, number>();

function actionTarget(target: Element): Element {
  return target.closest(ACTIONABLE) ?? target;
}

/** `button "Kaydet"` biçiminde kısa, okunur bir ad. */
export function describeElement(target: Element): string {
  const el = actionTarget(target);
  const text = ((el as HTMLElement).innerText ?? el.textContent ?? '').replace(/\s+/g, ' ').trim();
  const name =
    el.getAttribute('aria-label') ||
    text ||
    el.getAttribute('title') ||
    el.getAttribute('placeholder') ||
    el.getAttribute('data-testid') ||
    el.getAttribute('name') ||
    el.id;
  const tag = el.tagName.toLowerCase();
  return name ? `${tag} "${name.slice(0, LABEL_MAX)}"` : tag;
}

/** Son 15 saniyedeki tıklama; daha eskiyse rapora eklenmez. */
export function getLastAction(now = Date.now()): { label: string; ms_ago: number } | undefined {
  if (!last || now - last.at > ACTION_FRESH_MS) return undefined;
  return { label: last.label, ms_ago: now - last.at };
}

export function handleClick(target: Element, now: number, onRage: (label: string) => void): void {
  const el = actionTarget(target);
  const label = describeElement(el);
  last = { label, at: now };
  if (el.closest('[data-no-rage]')) return;
  if (burst.el !== el) burst = { el, times: [] };
  burst.times = [...burst.times.filter((t) => now - t <= RAGE_WINDOW_MS), now];
  if (burst.times.length < RAGE_CLICKS) return;
  burst.times = [];
  const previous = rageReported.get(el);
  if (previous !== undefined && now - previous <= RAGE_COOLDOWN_MS) return;
  rageReported.set(el, now);
  onRage(label);
}

/** Tıklama dinleyicisini kurar; kaldırıcıyı döndürür. */
export function installClickTracking(onRage: (label: string) => void): () => void {
  const listener = (event: MouseEvent): void => {
    if (event.target instanceof Element) handleClick(event.target, Date.now(), onRage);
  };
  document.addEventListener('click', listener, true);
  return () => document.removeEventListener('click', listener, true);
}

export function resetLastAction(): void {
  last = null;
  burst = { el: null, times: [] };
  rageReported = new WeakMap<Element, number>();
}
